import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, first, map, Observable, switchMap } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { CanvasElementId } from '@blaze-runtime/engine-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { isDeepEqual, isDefined } from '@blaze-shared/shared-util';

import { ElementDefinitionsService } from '../../../../services/element-definitions.service';
import { ElementStoreViewService } from '../../../../services/element-store-view.service';
import { findParentFormGroupLikeOfForm } from '../../../../utils/layout/relationship';
import { DataProviderQuery, InputDataPackage } from '../../io-data.models';

import { DataSourceProvider } from './data-source.data-provider';

@Injectable({ providedIn: 'root' })
export class FormGroupDataProvider implements DataSourceProvider {
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly schemaService = inject(SchemaService);
  private readonly elementStoreView = inject(ElementStoreViewService);
  private readonly store = inject(Store);
  readonly sourceType = 'formGroup' as const;

  getDataStream({ source, element }: DataProviderQuery): Observable<InputDataPackage> {
    const elementId: CanvasElementId = element.id;
    return this.elementStoreView.entities$.pipe(
      first(),
      map((elements) => findParentFormGroupLikeOfForm(elements, elementId)),
      filter(isDefined),
      switchMap((formGroup) =>
        this.store.select(
          runtimeSelectors.selectOutputUiDataById(
            formGroup.id,
            this.elementDefinitionsService.getDefaultOutputEventType(formGroup.type),
          ),
        ),
      ),
      map(
        (data) =>
          <InputDataPackage>{
            sourceType: 'formGroup',
            data,
            schema: this.schemaService.getSchema(source.id),
          },
      ),
      distinctUntilChanged(isDeepEqual),
    );
  }
}
